import { Theme } from "@material-ui/core";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { MuiTheme } from "theme";

interface IStylesProps {
  backgroundColor: string;
  strokeColor: string;
}

export default makeStyles<Theme, IStylesProps>((theme: MuiTheme) => ({
  card: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    position: "relative",
    minHeight: 60,
    height: 60,
    width: 60,
    minWidth: 60,
    marginRight: 10,
    borderRadius: 5,
    marginTop: 10,
    overflow: "visible",
    cursor: "pointer"
  },
  default: {
    borderColor: "transparent",
    boxShadow: "0 1px 7px 0 rgb(0, 0, 0, 0.08)"
  },
  cardDisabled: {
    pointerEvents: "none",
    border: "1px solid #d8d8d8",
    backgroundColor: "#eeeeee",
    boxShadow: "none",
    "& $dayName": {
      color: "#d8d8d8"
    },
    "& $date": {
      color: "#d8d8d8"
    },
    "& $cardHeader, & $cardActions": {
      display: "none"
    }
  },
  cardDetailed: {
    borderRadius: 5,
    minHeight: 77,
    borderWidth: 1,
    borderStyle: "solid",
    color: ({ backgroundColor }) =>
      backgroundColor ? `#${backgroundColor}` : theme.palette.text.primary,
    backgroundColor: ({ backgroundColor }) =>
      backgroundColor ? `#${backgroundColor}` : theme.palette.common.white,
    borderColor: ({ strokeColor }) =>
      strokeColor ? `#${strokeColor}` : "transparent",
    [theme.breakpoints.down("md")]: {
      height: 77
    },
    [theme.breakpoints.up("md")]: {
      minHeight: 78
    },
    "& $cardContent": {
      maxHeight: "none",
      justifyContent: "center"
    },
    "& $day": {
      fontSize: 8,
      top: -10
    },
    "& $cardHeader": {
      borderRadius: 0,
      fontSize: 14,
      height: 16,
      [theme.breakpoints.up("md")]: {
        height: 22
      },
      [theme.breakpoints.up("xl")]: {
        height: 30,
        fontSize: 16
      }
    },
    "&$cardDisabled $cardDetailedDefaultHeader": {
      backgroundColor: "rgba(216, 216, 216, 0.25)"
    },
    "& $cardDetailedDefaultHeader": {
      height: 10
    },
    "& $cardDetailedDefaultActions": {
      height: 10,
      maxHeight: 10,
      marginBottom: 0
    },
    "& $dayName": {
      display: "none"
    },
    "& $date": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      lineHeight: "normal",
      marginBottom: 0,
      [theme.breakpoints.down("md")]: {
        fontSize: 18,
        width: 26,
        height: 26
      },
      [theme.breakpoints.up("md")]: {
        fontSize: 24,
        width: 36,
        height: 35
      },
      [theme.breakpoints.up("lg")]: {
        width: 44,
        height: 39
      }
    },
    "& $cardActions": {
      height: 14,
      marginBottom: 8,
      [theme.breakpoints.down("sm")]: {
        marginBottom: 4
      },
      [theme.breakpoints.up("sm")]: {
        height: 18
      },
      [theme.breakpoints.up("lg")]: {
        height: 18
      }
    },
    "& $commentIcon": {
      fontSize: 10
    },
    "& $today": {
      display: "none"
    },
    "& $moreHorizIcon": {
      fontSize: 30,
      [theme.breakpoints.down("sm")]: {
        fontSize: 24
      }
    },
    "& $squared": {
      padding: 4,
      borderRadius: 6
    }
  },
  cardHeader: {
    position: "absolute",
    top: 0,
    left: -1,
    right: -1,
    "& > div": {
      overflow: "hidden"
    },
    borderRadius: "3px 3px 0 0",
    padding: 0,
    fontSize: 10,
    marginBottom: "0",
    whiteSpace: "nowrap",
    textAlign: "center",
    paddingLeft: 0,
    paddingRight: 0,
    backgroundColor: ({ strokeColor }) =>
      strokeColor ? `#${strokeColor}` : theme.palette.primary.main,
    color: ({ backgroundColor }) =>
      backgroundColor ? `#${backgroundColor}` : theme.palette.common.white
  },
  cardHeaderFiller: {
    "& > div": {
      overflow: "hidden"
    },
    borderRadius: "2px 2px 0 0",
    backgroundColor: "rgba(216, 216, 216, 0.25)",
    padding: 0,
    height: 9,
    fontSize: 9,
    marginBottom: "0",
    whiteSpace: "nowrap",
    textAlign: "center",
    paddingLeft: 0,
    paddingRight: 0
  },
  cardDetailedDefaultHeader: {
    backgroundColor: "rgba(238, 238, 238, 0.5)"
  },
  cardDetailedDefaultActions: {},
  squared: {
    color: ({ backgroundColor }) =>
      backgroundColor ? `#${backgroundColor}` : "transparent",
    borderRadius: 2,
    border: "2px solid"
  },
  cardContent: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    justifyContent: "center",
    padding: 0,
    maxHeight: 49
  },
  cardActions: {
    position: "absolute",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    height: 16,
    maxHeight: 16,
    bottom: 0,
    left: 0,
    right: 0,
    overflow: "hidden"
  },
  moreHorizIcon: {
    fontSize: 18,
    color: ({ strokeColor }) =>
      strokeColor ? `#${strokeColor}` : theme.palette.text.primary
  },
  commentIcon: {
    fontSize: 9,
    color: "black",
    opacity: 0.4,
    position: "absolute",
    right: 2,
    bottom: 1
  },
  day: {
    fontSize: 8,
    top: -10
  },
  dayName: {
    position: "absolute",
    top: -14,
    padding: 0,
    lineHeight: 1,
    fontSize: 10,
    width: "100%",
    textAlign: "center",
    color: ({ strokeColor }) =>
      strokeColor ? `#${strokeColor}` : theme.palette.text.primary
  },
  dateWrapper: {
    position: "relative",
    padding: 0
  },
  date: {
    lineHeight: "normal",
    width: 24,
    height: 22,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
    fontWeight: "bold",
    textAlign: "center",
    color: ({ strokeColor }) =>
      strokeColor ? `#${strokeColor}` : theme.palette.text.primary
  },
  dateMonth: {
    position: "absolute",
    textAlign: "center",
    left: "-50%",
    right: "-50%",
    top: -13,
    fontSize: 11,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    [theme.breakpoints.down("sm")]: {
      fontSize: 10,
      top: -11
    },
    [theme.breakpoints.up("sm")]: {
      fontSize: 11,
      top: -12
    },
    [theme.breakpoints.up("md")]: {
      fontSize: 11
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: 12
    }
  },
  today: {
    color: theme.palette.text.primary,
    position: "absolute",
    top: -13,
    fontSize: 8
  },
  lastDay: {
    color: theme.palette.text.primary,
    position: "absolute",
    top: -13,
    right: 0,
    fontSize: 8
  },
  hasComments: {
    backgroundColor: "#000000"
  },
  loader: {
    position: "absolute",
    zIndex: 1,
    top: -1,
    left: -1,
    width: "calc(100% + 2px)",
    height: "calc(100% + 2px)",
    borderRadius: 3,
    background: "rgba(255, 255, 255, 0.85)",
    padding: 0
  }
}));
